/* rt-perf-pass:v1 */
/* ============================================================
   rt-booth.js — DJ booth engine: two Web Audio decks, rotary mixer,
   crate, record-wall bridge and sticky mini transport.
   Consolidated 2026-08-21 from snippets/rt-mixer-b01..b11 (one HTML
   document split byte-wise into 6 KB chunks, cut mid-function).
   Configured by the JSON <script id="rt-booth-config"> emitted by
   sections/rt-mixer.liquid. Crate data comes from the /pages/rt-tracks
   feed (yoyaku mode) or the inline #rt-booth-data JSON (records mode,
   snippets/rt-booth-data.liquid).
   ============================================================ */
(function(){
  "use strict";

  // The section markup (sections/rt-mixer.liquid) must be on the page.
  if (!document.getElementById("rt-console")) return;

  /* ================= CONFIG ================= */
  // sections/rt-mixer.liquid emits <script type="application/json"
  // id="rt-booth-config"> from its settings before this asset loads.
  var cfgEl = document.getElementById("rt-booth-config");
  var CFG = {};
  if (cfgEl) { try { CFG = JSON.parse(cfgEl.textContent) || {}; } catch (e) { CFG = {}; } }

  // Records mode is desktop-only; the section is hidden under 750px.
  // 960, not 750: the booth lives in the rail now and the rail is hidden below
  // 960px, so anything narrower has no booth to hear. NOT conditioned on
  // CFG.inline any more - pages that fall back to the feed would otherwise
  // preload two previews over cellular for a booth the phone never shows.
  var NARROW_RECORDS = window.innerWidth < 960;

  // UI strings come from the section (EN/FR); T() falls back to the English
  // literal so the engine still reads correctly if a key is ever missing.
  var I18N = CFG.i18n || {};
  function T(key, fallback){ return I18N[key] || fallback; }

  /* rt-youtube-decks:v1 — a deck can play a YouTube video through the IFrame
     API where the track's only clip is a 30-second Apple preview, so the deck
     holds the whole track. The API script loads once, on first use; a player
     is built per deck on its first start, inside the user's gesture (built at
     page load it could not sound, and two iframes per product page for a
     booth nobody has pressed yet is a heavy page for nothing). What the API
     gives: play, pause, seek, volume, and a playback rate the player rounds
     to YT_RATE_STEP. What it does not: the samples - so no Web Audio EQ on a
     video deck, and its BPM comes from the Apple preview of the same track,
     decoded silently just for the count. */
  var YT_RATE_STEP = 0.05;
  var YT_QUEUE = [];
  function ytFlush(ok){ var q = YT_QUEUE.slice(); YT_QUEUE.length = 0; q.forEach(function(f){ try { f(ok); } catch(e){} }); }
  /* Loads the IFrame API once and calls back with ok=true, or ok=false when
     the script cannot arrive (an ad or privacy blocker, offline, a stall):
     the dead tag is removed so a later click can try again instead of queuing
     behind it forever, and the deck falls back to its clip. */
  function ytEnsure(cb){
    if (window.YT && window.YT.Player) { cb(true); return; }
    YT_QUEUE.push(cb);
    if (document.getElementById("rt-ytapi")) return;
    var prev = window.onYouTubeIframeAPIReady, t = null;
    window.onYouTubeIframeAPIReady = function(){
      if (t) { clearTimeout(t); t = null; }
      if (typeof prev === "function") { try { prev(); } catch(e){} }
      ytFlush(true);
    };
    var s = document.createElement("script"); s.id = "rt-ytapi";
    function dead(){ if (t) { clearTimeout(t); t = null; } try { if (s.parentNode) s.parentNode.removeChild(s); } catch(e){} ytFlush(false); }
    s.onerror = dead;
    t = setTimeout(function(){ if (!(window.YT && window.YT.Player)) dead(); }, 9000);
    s.src = "https://www.youtube.com/iframe_api"; document.head.appendChild(s);
  }
  function isApple(u){ return /itunes\.apple\.com|mzstatic\.com/i.test(u||""); }
  function ytIdFrom(u){
    if (!u) return "";
    var s = String(u).trim();
    var m = s.match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
    if (m) return m[1];
    return /^[A-Za-z0-9_-]{11}$/.test(s) ? s : "";
  }

  /* The video monitor: where a video deck's player actually lives. YouTube's
     terms want the player visible and unobscured while it sounds, at 200px or
     more - the platter box in the rail is 107px, and the record belongs there
     anyway. So each deck owns a 356x200 pane in a floating strip at the
     bottom right, shown only while that deck holds a video. */
  var MON=null;
  function ensureMonitor(){
    if(MON) return MON;
    MON=document.createElement("div"); MON.id="rt-monitor";
    // The label sits ABOVE the player, never on it: YouTube's terms forbid
    // any overlay in front of any part of an embedded player. It also names
    // the source, which the same terms ask for.
    MON.innerHTML='<div class="rt-mon a"><span class="rt-mon-tag"><b>A</b> &middot; YouTube</span><div class="rt-mon-host"></div></div>'+
                  '<div class="rt-mon b"><span class="rt-mon-tag"><b>B</b> &middot; YouTube</span><div class="rt-mon-host"></div></div>';
    document.body.appendChild(MON);
    return MON;
  }
  function updateMonitor(){ if(MON) MON.classList.toggle("on", !!MON.querySelector(".rt-mon.on")); }
  function parseDur(s){
    if(!s) return 0;
    var p=String(s).trim().split(":");
    if(p.some(function(x){ return x===""||isNaN(x); })) return 0;
    var n=0; for(var i=0;i<p.length;i++) n=n*60+parseFloat(p[i]);
    return n>0?n:0;
  }
  function fmtTime(sec){
    if(!(sec>=0)||!isFinite(sec)) return "–:––";
    sec=Math.floor(sec); var m=Math.floor(sec/60), s=sec%60;
    return m+":"+(s<10?"0":"")+s;
  }
  // peak per bin over every channel, normalised - the shape of the clip
  function computePeaks(buffer,n){
    var chs=buffer.numberOfChannels, len=buffer.length, per=Math.max(1,Math.floor(len/n));
    var out=new Float32Array(n), max=0;
    for(var c=0;c<chs;c++){
      var d=buffer.getChannelData(c);
      for(var i=0;i<n;i++){
        var s=i*per, e=Math.min(len,s+per), pk=0;
        for(var j=s;j<e;j+=2){ var v=d[j]<0?-d[j]:d[j]; if(v>pk) pk=v; }
        if(pk>out[i]) out[i]=pk; if(pk>max) max=pk;
      }
    }
    if(max>0) for(var k=0;k<n;k++) out[k]/=max;
    return out;
  }

  /* The shop wordmark, painted into the centre label of both records. It is
     only ever drawn - never read back - so a cross-origin CDN image tainting
     the canvas costs nothing. The render loop runs every frame, so the label
     picks it up by itself the moment it decodes. */
  var LOGO = null;
  if (CFG.labelIcon) { LOGO = new Image(); LOGO.src = CFG.labelIcon; }

  /* Tempo measured from the audio itself.

     The GROOVES table below is a leftover from when the booth had no real
     clips and synthesised drum patterns instead. Each record was hashed onto
     one of its six entries, which is where rec.bpm and rec.lbl came from - so
     every record displayed one of six fabricated tempos, and SYNC beatmatched
     to them. With real previews playing, that number has to come from the
     audio.

     Same approach as needle_drop.py, minus the FFT: low-pass to leave the
     kick, take the rising edge of the envelope, autocorrelate over the
     plausible beat range, then fold octave errors into house/techno territory.
     Cheap enough to run once per decoded buffer. */
  function detectBpm(buffer){
    try{
      var sr=buffer.sampleRate, n=buffer.length;
      if(!n || !sr) return 0;
      var chs=buffer.numberOfChannels, mono=new Float32Array(n);
      for(var c=0;c<chs;c++){
        var d=buffer.getChannelData(c);
        for(var i=0;i<n;i++) mono[i]+=d[i]/chs;
      }
      // one-pole low-pass ~250Hz: below it, only the kick survives
      var rc=1/(2*Math.PI*250), dt=1/sr, a=dt/(rc+dt), lp=0;
      var step=Math.max(1,Math.round(sr/200)), m=Math.floor(n/step);
      if(m<64) return 0;
      var env=new Float32Array(m), k=0, peak=0;
      for(var i=0;i<n;i++){
        lp+=a*(mono[i]-lp);
        var v=lp<0?-lp:lp;
        if(v>peak) peak=v;
        if(i%step===step-1 && k<m){ env[k++]=peak; peak=0; }
      }
      var on=new Float32Array(m);
      for(var i=1;i<m;i++){ var df=env[i]-env[i-1]; on[i]=df>0?df:0; }
      var fps=sr/step;
      var lo=Math.floor(fps*60/160), hi=Math.ceil(fps*60/88);
      if(hi>=m) hi=m-1;
      var best=-1, bestLag=0;
      for(var lag=lo; lag<=hi; lag++){
        var sum=0;
        for(var i=0;i+lag<m;i++) sum+=on[i]*on[i+lag];
        sum/=(m-lag);
        if(sum>best){ best=sum; bestLag=lag; }
      }
      if(!bestLag) return 0;
      var bpm=60*fps/bestLag;
      while(bpm<112 && bpm*2<=170) bpm*=2;
      while(bpm>140 && bpm/2>=80) bpm/=2;
      if(!isFinite(bpm) || bpm<60 || bpm>200) return 0;
      return Math.round(bpm*10)/10;
    }catch(e){ return 0; }
  }

  /* rt-audio-truce:v1 — same contract as assets/player.js: announce when a
     deck starts, stop both decks when any other source starts. */
  function rtBoothAnnounce(){ document.dispatchEvent(new CustomEvent("rt:audio-play",{detail:{source:"booth"}})); }
  document.addEventListener("rt:audio-play", function(ev){
    var s = ev.detail && ev.detail.source;
    if (s === "booth") return;
    // Clear pending autoplay too: a deck whose preview is still decoding would
    // otherwise start seconds later, on top of whatever the user just played.
    if (deckA) { deckA.autoplayWhenReady = false; if (deckA.playing) deckA.stop(); }
    if (deckB) { deckB.autoplayWhenReady = false; if (deckB.playing) deckB.stop(); }
  });

  /* ================= STORE (live Shopify catalog) ================= */
  // Records come straight from your shop — only real inventory is loadable.
  var STORE = { base:"", collection:(CFG.collection||"yoyaku-in-stock"),
                currency:(CFG.currency||"$"),
                feed:(CFG.feed||"/pages/rt-tracks") };
  // The Liquid feed at STORE.feed is the source of truth: title, artist (from the
  // custom.artists-string metafield), price, image, and preview audio (custom.yy_audios).
  var AUDIO = {}; // kept empty; only used by the products.json fallback below.
  function mapFeedItem(it){
    var g=pickGroove(it.handle||it.title);
    return { title:it.title, artist:(it.artist||""), price:(it.price||""),
             available:(it.available!==false),
             url:STORE.base+"/products/"+it.handle, image:(it.image||""),
             audioUrl:(it.audio||null),
             ytId:(ytIdFrom(it.yt)||null), bpmUrl:(it.bpm||null), dur:(it.dur||""),
             bpm:g.bpm, lbl:g.lbl, wave:g.wave, cutoff:g.cutoff, swing:g.swing, acid:g.acid,
             kick:g.kick, snare:g.snare, hat:g.hat, bass:g.bass };
  }

  /* Groove bank = the audible "turntable sound". Each real record is matched to
     one of these so a mix has sound (Shopify carries no audio). Add real preview
     clips later and these become the fallback. */
  var GROOVES = [
    { bpm:122, lbl:"#e8a020", wave:"sawtooth", cutoff:520, swing:0,
      kick:[1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0], snare:[0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0],
      hat:[0,0,1,0,0,0,1,0,0,0,1,0,0,0,2,0], bass:[55,0,0,55,0,0,65.41,0,55,0,0,82.41,0,0,65.41,0] },
    { bpm:126, lbl:"#4a6d7c", wave:"triangle", cutoff:240, swing:0,
      kick:[1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0], snare:[0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
      hat:[0,0,0,0,0,0,1,0,0,0,0,0,0,0,1,0], bass:[55,0,0,0,0,0,0,0,65.41,0,0,0,0,0,49,0] },
    { bpm:132, lbl:"#c7413a", wave:"square", cutoff:420, swing:0,
      kick:[1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0], snare:[0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0],
      hat:[0,0,1,0,0,0,1,0,0,0,1,0,0,0,2,0], bass:[0,0,55,0,0,0,55,0,0,0,65.41,0,0,0,65.41,0] },
    { bpm:118, lbl:"#e86fa6", wave:"sawtooth", cutoff:900, swing:0,
      kick:[1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0], snare:[0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0],
      hat:[0,0,2,0,0,0,2,0,0,0,2,0,0,0,2,0], bass:[55,0,110,0,55,0,110,0,65.41,0,130.81,0,65.41,0,130.81,0] },
    { bpm:128, lbl:"#b6e820", wave:"sawtooth", cutoff:340, swing:0, acid:true,
      kick:[1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0], snare:[0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0],
      hat:[0,0,1,0,0,0,1,0,0,0,1,0,0,0,2,0], bass:[55,55,0,82.41,55,0,65.41,55,0,82.41,55,0,98,0,82.41,0] },
    { bpm:92, lbl:"#c98a3a", wave:"triangle", cutoff:300, swing:.18,

      kick:[1,0,0,0,0,0,1,0,0,0,1,0,0,0,0,0], snare:[0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0],
      hat:[1,0,1,0,1,0,1,0,1,0,1,0,1,0,1,0], bass:[55,0,0,0,0,0,55,0,0,0,65.41,0,0,0,0,0] }
  ];
  function pickGroove(key){ var h=0,s=String(key); for(var i=0;i<s.length;i++){h=(h*31+s.charCodeAt(i))>>>0;} return GROOVES[h%GROOVES.length]; }
  function esc(s){ return String(s==null?"":s).replace(/[&<>\"]/g,function(c){return {"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;"}[c];}); }
  function mapProduct(p){
    var g=pickGroove(p.handle||p.id);
    var img=(p.images&&p.images[0]&&p.images[0].src)||(p.image&&p.image.src)||"";
    var v=(p.variants&&p.variants[0])||{};
    var avail=!p.variants||p.variants.some(function(x){return x.available;});
    return { title:p.title, artist:p.vendor||"", price:(v.price?STORE.currency+v.price:""), available:avail,
             url:STORE.base+"/products/"+p.handle, image:img, audioUrl:(AUDIO[p.handle]||null),
             bpm:g.bpm, lbl:g.lbl, wave:g.wave, cutoff:g.cutoff, swing:g.swing, acid:g.acid,
             kick:g.kick, snare:g.snare, hat:g.hat, bass:g.bass }; }

  /* ================= AUDIO ENGINE ================= */
  var ac=null, master=null, noiseBuf=null;
  var MASTER=0.85;              // the master knob, mirrored onto video decks (they bypass the bus)
  var eqBass=null, eqTreble=null;
  function initAudio(){
    if(ac) return;
    ac=new (window.AudioContext||window.webkitAudioContext)();
    master=ac.createGain(); master.gain.value=0.85;
    eqBass=ac.createBiquadFilter(); eqBass.type="lowshelf"; eqBass.frequency.value=120; eqBass.gain.value=0;
    eqTreble=ac.createBiquadFilter(); eqTreble.type="highshelf"; eqTreble.frequency.value=3500; eqTreble.gain.value=0;
    master.connect(eqBass); eqBass.connect(eqTreble); eqTreble.connect(ac.destination);
    var n=ac.sampleRate*1; noiseBuf=ac.createBuffer(1,n,ac.sampleRate);
    var d=noiseBuf.getChannelData(0); for(var i=0;i<n;i++) d[i]=Math.random()*2-1;
  }
  function kick(t,g){var o=ac.createOscillator(),e=ac.createGain();o.type="sine";
    o.frequency.setValueAtTime(150,t);o.frequency.exponentialRampToValueAtTime(50,t+.11);
    e.gain.setValueAtTime(1,t);e.gain.exponentialRampToValueAtTime(.001,t+.30);
    o.connect(e);e.connect(g);o.start(t);o.stop(t+.32);}
  function snare(t,g){var s=ac.createBufferSource();s.buffer=noiseBuf;var bp=ac.createBiquadFilter();
    bp.type="bandpass";bp.frequency.value=1800;bp.Q.value=.8;var e=ac.createGain();
    e.gain.setValueAtTime(.7,t);e.gain.exponentialRampToValueAtTime(.001,t+.18);
    s.connect(bp);bp.connect(e);e.connect(g);s.start(t);s.stop(t+.2);
    var o=ac.createOscillator(),e2=ac.createGain();o.type="triangle";o.frequency.value=180;
    e2.gain.setValueAtTime(.4,t);e2.gain.exponentialRampToValueAtTime(.001,t+.12);
    o.connect(e2);e2.connect(g);o.start(t);o.stop(t+.13);}
  function hat(t,g,open){var s=ac.createBufferSource();s.buffer=noiseBuf;var hp=ac.createBiquadFilter();
    hp.type="highpass";hp.frequency.value=7200;var e=ac.createGain();var dur=open?.30:.05;
    e.gain.setValueAtTime(open?.32:.28,t);e.gain.exponentialRampToValueAtTime(.001,t+dur);
    s.connect(hp);hp.connect(e);e.connect(g);s.start(t);s.stop(t+dur+.02);}
  function bassHit(t,f,g,r,step){var o=ac.createOscillator(),fl=ac.createBiquadFilter(),e=ac.createGain();
    o.type=r.wave;o.frequency.value=f;fl.type="lowpass";fl.Q.value=r.acid?12:1;
    if(r.acid){fl.frequency.setValueAtTime(r.cutoff*3,t);fl.frequency.exponentialRampToValueAtTime(r.cutoff,t+step*.9);}
    else fl.frequency.value=r.cutoff;var dur=step*.9;
    e.gain.setValueAtTime(.0001,t);e.gain.exponentialRampToValueAtTime(.6,t+.01);e.gain.exponentialRampToValueAtTime(.001,t+dur);
    o.connect(fl);fl.connect(e);e.connect(g);o.start(t);o.stop(t+dur+.02);}

  /* ================= DECK ================= */
  function Deck(side){
    this.side=side; this.rec=null; this.pitch=0; this.bend=0; this.cuePreview=false; this.playing=false;
    this.speed=1;                 // 33 rpm reference; 45 is exactly 1.35x
    this.autoplayWhenReady=false;
    this.step=0; this.nextTime=0; this.timer=null; this.angle=0; this.vel=0;
    this.chGain=null; this.chVol=1;
    this.buffer=null; this.src=null; this.loadingId=0;
    this.mode="none"; this.yt=null; this.ytReady=false; this.ytWant=false; this.ytGen=0; this.ytBuilding=false;
    this.pos=0; this.dur=0; this.durHint=0; this.peaks=null; this.seekWanted=null; this._waveW=0; this._waveT=0;
    this.build();
  }
  Deck.prototype.build=function(){
    var s=this.side, mirror=s==="b";
    var el=document.createElement("section"); el.className="rt-deck "+s; el.id="deck-"+s;
    el.innerHTML=
      '<div class="rt-face">'+
        '<div class="rt-speeds"><button class="rt-speed on">33</button><button class="rt-speed">45</button></div>'+
        '<div class="rt-power"><span class="led"></span></div>'+
        '<div class="rt-platter"><canvas class="rt-vinyl" width="380" height="380" aria-hidden="true"></canvas></div>'+
        '<svg class="rt-arm" viewBox="0 0 96 120" aria-hidden="true">'+
          '<circle cx="80" cy="14" r="9" fill="#3a3a3e" stroke="#1a1a1c"/>'+
          '<rect x="74" y="4" width="12" height="9" rx="2" fill="#8a8a90"/>'+
          '<path d="M80 20 Q54 40 44 78 Q40 92 34 104" fill="none" stroke="#c9c9cf" stroke-width="4" stroke-linecap="round"/>'+
          '<rect x="26" y="100" width="16" height="10" rx="2" fill="#2a2a2e" transform="rotate(20 34 105)"/>'+
        '</svg>'+
        '<div class="rt-target"></div>'+
        '<div class="rt-pitch"><div class="rt-strobe lock"></div>'+
          '<div class="rt-pitch-track"><input type="range" class="rt-pitch-input" min="-8" max="8" step="0.05" value="0" aria-label="Pitch deck '+s.toUpperCase()+'"></div>'+
        '</div>'+
        '<button class="rt-startstop" aria-label="Start/stop deck '+s.toUpperCase()+'" aria-pressed="false"></button>'+


      '</div>'+
      '<div class="rt-info">'+
        '<div class="rt-wave-row">'+
          '<button class="rt-range rt-nudge" data-n="-1" title="Nudge slower (hold)" aria-label="Nudge deck '+s.toUpperCase()+' slower">&#9666;</button>'+
          '<div class="rt-wave" role="slider" aria-label="Position deck '+s.toUpperCase()+'" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><canvas class="rt-wave-c" aria-hidden="true"></canvas><span class="rt-wave-fill"></span><span class="rt-wave-ph"></span><span class="rt-wave-t l">0:00</span><span class="rt-wave-t r">&ndash;:&ndash;&ndash;</span></div>'+
          '<button class="rt-range rt-nudge" data-n="1" title="Nudge faster (hold)" aria-label="Nudge deck '+s.toUpperCase()+' faster">&#9656;</button>'+
          '<span class="rt-src-tag"></span>'+
        '</div>'+
        '<div class="rt-nowplay"><img class="rt-cover" alt="" width="30" height="30" style="display:none"><div class="rt-nowtext"><a class="rt-track-name" rel="noopener">'+T("noRecord","No record loaded")+'</a><div class="rt-track-sub">'+T("pickFromCrate","pick from the crate")+'</div></div></div>'+
        '<div class="rt-readout"><b class="rt-pitch-val">0.0%</b> &middot; <b class="rt-bpm-val">&mdash;</b> BPM</div>'+
        '<div class="rt-deck-actions"><button class="rt-range rt-rangebtn" aria-label="Pitch range deck '+s.toUpperCase()+', currently plus or minus 8 percent">&plusmn;8</button><button class="rt-cue">'+T("cue","cue")+'</button><a class="rt-buy" href="#" target="_blank" rel="noopener">'+T("buy","buy")+'</a></div>'+
      '</div>';
    document.getElementById("rt-console").appendChild(el);
    this.el=el;
    this.startBtn=el.querySelector(".rt-startstop");
    this.cueBtn=el.querySelector(".rt-cue");
    this.buyBtn=el.querySelector(".rt-buy");
    this.nameEl=el.querySelector(".rt-track-name");
    this.subEl=el.querySelector(".rt-track-sub");
    this.bpmEl=el.querySelector(".rt-bpm-val");
    this.pitchInput=el.querySelector(".rt-pitch-input");
    this.pitchVal=el.querySelector(".rt-pitch-val");
    this.rangeBtn=el.querySelector(".rt-rangebtn"); this.range=8;
    this.strobe=el.querySelector(".rt-strobe");
    this.canvas=el.querySelector(".rt-vinyl");
    this.coverEl=el.querySelector(".rt-cover");
    var mon=ensureMonitor();
    this.monEl=mon.querySelector(".rt-mon."+s);
    this.videoEl=this.monEl.querySelector(".rt-mon-host");
    this.srcTag=el.querySelector(".rt-src-tag");
    this.waveEl=el.querySelector(".rt-wave");
    this.waveC=el.querySelector(".rt-wave-c");
    this.waveFill=el.querySelector(".rt-wave-fill");
    this.wavePh=el.querySelector(".rt-wave-ph");
    this.waveL=el.querySelector(".rt-wave-t.l");
    this.waveR=el.querySelector(".rt-wave-t.r");
    this.ctx=this.canvas.getContext("2d");
    this.setIcon(false); this.draw();
    var self=this;
    this.startBtn.addEventListener("click",function(){self.toggle();});
    // The 33/45 buttons were decoration: rendered with an .on class and no
    // handler at all, so clicking them did nothing. A 33 played at 45 runs
    // 45/(100/3) = 1.35x fast, which is what the platter, the rate and the
    // BPM readout all follow from.
    var speeds=el.querySelectorAll(".rt-speed");
    for(var si=0;si<speeds.length;si++)(function(sb,idx){
      sb.setAttribute("aria-pressed", idx===0?"true":"false");
      sb.addEventListener("click",function(){
        self.speed = idx===0 ? 1 : 1.35;
        for(var j=0;j<speeds.length;j++){
          speeds[j].classList.toggle("on", j===idx);
          speeds[j].setAttribute("aria-pressed", j===idx?"true":"false");
        }
        self.updateBpm();
        self.applyRate();
      });
    })(speeds[si], si);
    this.cueBtn.addEventListener("pointerdown",function(e){ e.preventDefault(); self.cueDown(); });
    this.cueBtn.addEventListener("pointerup",function(){ self.cueUp(); });
    this.cueBtn.addEventListener("pointercancel",function(){ self.cueUp(); });
    this.cueBtn.addEventListener("pointerleave",function(){ self.cueUp(); });
    var nbs=el.querySelectorAll(".rt-nudge");
    for(var ni=0;ni<nbs.length;ni++)(function(nb){
      var dir=parseFloat(nb.getAttribute("data-n"));
      nb.addEventListener("pointerdown",function(e){ e.preventDefault(); self.nudge(dir,true); });
      nb.addEventListener("pointerup",function(){ self.nudge(dir,false); });
      nb.addEventListener("pointercancel",function(){ self.nudge(dir,false); });
      nb.addEventListener("pointerleave",function(){ if(self.bend!==0) self.nudge(dir,false); });
    })(nbs[ni]);
    this.pitchInput.addEventListener("input",function(){
      self.pitch=parseFloat(this.value);
      self.paintPitch();
      self.strobe.classList.toggle("lock",Math.abs(self.pitch)<0.05);
      self.updateBpm();
      self.applyRate();
    });
    this.rangeBtn.addEventListener("click",function(){
      self.range = self.range===8 ? 16 : 8;
      self.rangeBtn.textContent = "±"+self.range;
      self.rangeBtn.setAttribute("aria-label","Pitch range deck "+s.toUpperCase()+", currently plus or minus "+self.range+" percent");
      self.pitchInput.min = -self.range; self.pitchInput.max = self.range;
      if(self.pitch > self.range) self.pitch = self.range;
      if(self.pitch < -self.range) self.pitch = -self.range;
      self.pitchInput.value = self.pitch;
      self.paintPitch();
      self.strobe.classList.toggle("lock",Math.abs(self.pitch)<0.05);
      self.updateBpm();
      self.applyRate();
    });
    // the strip is the track: click anywhere on it to drop the needle there
    this.waveEl.addEventListener("pointerdown",function(e){
      e.preventDefault();
      var total=self.dur||self.durHint; if(!total) return;
      var r=self.waveEl.getBoundingClientRect(); if(!r.width) return;
      self.seek(Math.max(0,Math.min(1,(e.clientX-r.left)/r.width))*total);
    });
  };
  /* The measured tempo when we have one. The groove table's number is only
     used where the synth is actually the sound source; with real previews a
     fabricated tempo is worse than none, so the readout shows an em dash
     until the clip has been analysed. */
  Deck.prototype.baseBpm=function(){
    if(this.bpmDetected) return this.bpmDetected;
    if(CFG.synthFallback === false) return 0;
    return this.rec?this.rec.bpm:0;
  };
  /* What the sound source actually runs at. Web Audio takes any rate; the
     YouTube player rounds to YT_RATE_STEP, and the readout, the BPM and the
     platter follow the real rate rather than the fader's wish. */
  Deck.prototype.actualRate=function(){
    var r=this.rate();
    if(this.mode==="video" && YT_RATE_STEP>0) r=Math.round(r/YT_RATE_STEP)*YT_RATE_STEP;
    return r;
  };
  Deck.prototype.paintPitch=function(){
    var shown=this.pitch;
    if(this.mode==="video" && YT_RATE_STEP>0){
      // the fader position the player honoured, in the fader's own terms
      shown=(this.actualRate()/this.speed-1)*100 - this.bend;
    }
    this.pitchVal.textContent=(shown>=0?"+":"")+shown.toFixed(1)+"%";
  };
  Deck.prototype.effBpm=function(){var b=this.baseBpm();return b?b*this.actualRate():0;};
  Deck.prototype.updateBpm=function(){var b=this.effBpm();this.bpmEl.textContent=b?b.toFixed(1):"—";};
  Deck.prototype.setIcon=function(p){
    this.startBtn.innerHTML=p
      ?'<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="5" width="4" height="14" fill="currentColor"/><rect x="14" y="5" width="4" height="14" fill="currentColor"/></svg>'
      :'<svg viewBox="0 0 9 12" aria-hidden="true"><path d="M9 6L0 11.1962V0.803848L9 6Z" fill="currentColor"/></svg>';
  };
  // rt-booth-lazy-autoload:v1 - opts.lazy puts the record on the deck (name,
  // sleeve, link, tag) but leaves the clip unfetched until the first play or
  // cue. Used only for the two records the booth seats itself on page load;
  // every user-initiated load still fetches at once. On the homepage those
  // two previews were the heaviest downloads on the page, for a booth nobody
  // had touched yet.
  Deck.prototype.load=function(rec,opts){
    this.rec=rec; this.buffer=null; this.bpmDetected=0; this.loadingId++;
    this.deferred=false;
    this.nameEl.textContent=rec.title;
    // the name is the way back to the record; soft-nav keeps the deck playing
    if(rec.url){ this.nameEl.setAttribute("href",rec.url); }
    else { this.nameEl.removeAttribute("href"); }
    var meta=(rec.artist||"")+(rec.artist&&rec.price?" · ":"")+(rec.price||"")+(rec.available?"":T("soldOut"," · sold out"));
    this._meta=meta; this.subEl.textContent=meta;
    this.buyBtn.href=rec.url||"#";
    if(this.coverEl){ if(rec.image){ this.coverEl.src=rec.image; this.coverEl.style.display=""; } else { this.coverEl.style.display="none"; } }
    this.updateBpm(); this.draw();
    // Source choice. A shop clip or yoyaku mp3 decodes into Web Audio - real
    // pitch, EQ and a measured BPM - so it always wins. A video beats a
    // 30-second Apple preview: the whole track is worth more than the
    // mechanics the preview would allow. Apple is the last resort.
    // A deck that was running keeps running on the NEW record: stop the old
    // source now, restart once the new one exists (a video straight away,
    // inside this same gesture; a clip when it has decoded).
    var wasPlaying=this.playing;
    if(wasPlaying) this.stop();
    this.unloadYt();
    var useVideo = !!rec.ytId && !(rec.audioUrl && !isApple(rec.audioUrl));
    this.mode = useVideo ? "video" : (rec.audioUrl ? "buffer" : "none");
    this.el.classList.toggle("has-video", useVideo);
    if(this.monEl){ this.monEl.classList.toggle("on", useVideo); updateMonitor(); }
    this.pos=0; this.dur=0; this.durHint=parseDur(rec.dur); this.peaks=null; this.seekWanted=null;
    this.drawWave(); this.paintWave(true);
    if(this.srcTag) this.srcTag.textContent = useVideo ? "YT" : (rec.audioUrl ? (isApple(rec.audioUrl) ? "30s" : "clip") : "");
    // the fader moves in whole percents on a video deck (a range input's grid
    // starts at its min, so a step of 5 from -8 would skip 0 and ±5); the
    // honoured rate is rounded to YouTube's 0.05 steps in actualRate()
    this.pitchInput.step = (useVideo && YT_RATE_STEP>0) ? "1" : "0.05";
    this.paintPitch();
    if(useVideo){
      if(this.videoEl) this.videoEl.innerHTML='<img alt="" loading="lazy" src="https://i.ytimg.com/vi/'+encodeURIComponent(rec.ytId)+'/mqdefault.jpg">';
      // the tempo readout still wants a count: decode the Apple preview of
      // the same track for its BPM only, never for playback
      var bpmSrc = rec.bpmUrl || (isApple(rec.audioUrl) ? rec.audioUrl : null);
      if(bpmSrc) this.loadBpm(bpmSrc);
      if(wasPlaying) this.start();
    } else if(rec.audioUrl){
      if(opts&&opts.lazy&&!wasPlaying){ this.deferred=true; return; }
      this.subEl.textContent=meta+T("loadingPreview"," · loading preview…");
      if(wasPlaying) this.autoplayWhenReady=true;
      this.loadBuffer(rec);
    }
  };
  // first gesture on a lazily seated deck: fetch now; start() spins silently
  // (synthFallback is off) until loadBuffer swaps the decoded clip in.
  Deck.prototype.fetchDeferred=function(){
    if(!this.deferred) return;
    this.deferred=false;
    this.subEl.textContent=(this._meta||"")+T("loadingPreview"," · loading preview…");
    this.loadBuffer(this.rec);
  };
  Deck.prototype.loadBpm=function(url){
    initAudio(); var self=this, id=++this.loadingId;
    fetch(url).then(function(r){ if(!r.ok) throw 0; return r.arrayBuffer(); })
      .then(function(a){ return ac.decodeAudioData(a); })
      .then(function(b){ if(id!==self.loadingId) return;
        self.bpmDetected=detectBpm(b); self.updateBpm(); })
      .catch(function(){});
  };
  Deck.prototype.unloadYt=function(){
    this.ytWant=false; this.ytGen++; this.ytBuilding=false;
    if(this.yt){ try{ this.yt.destroy(); }catch(e){} this.yt=null; }
    this.ytReady=false;
    if(this.videoEl) this.videoEl.innerHTML="";
  };
  // Built on the first start, inside the user's gesture, so the video may sound.
  Deck.prototype.ensureYt=function(then){
    var self=this;
    // a player that exists, or one still being built while the API loads,
    // is reused: the pending build's callback reads ytWant when it lands
    if(this.yt||this.ytBuilding){ if(this.ytReady) then(); else this.ytWant=true; return; }
    var id=(this.rec&&this.rec.ytId)||""; if(!id||!this.videoEl) return;
    this.ytWant=true; this.ytBuilding=true;
    var gen=this.ytGen;
    var host=document.createElement("div");
    this.videoEl.innerHTML=""; this.videoEl.appendChild(host);
    ytEnsure(function(ok){
      if(gen!==self.ytGen) return;
      self.ytBuilding=false;
      if(!ok||!(window.YT&&window.YT.Player)){ self.ytWant=false; self.videoFailed(); return; }
      try{
        self.yt=new YT.Player(host,{width:"100%",height:"100%",videoId:id,
          playerVars:{controls:0,rel:0,playsinline:1,modestbranding:1,disablekb:1,iv_load_policy:3},
          events:{
            onReady:function(){ if(gen!==self.ytGen) return;
              self.ytReady=true; self.applyVolume(); self.applyRate();
              if(self.ytWant){ self.ytWant=false; then(); } },
            onStateChange:function(e){ if(gen!==self.ytGen) return; self.onYtState(e.data); },
            onError:function(){ if(gen!==self.ytGen) return; self.videoFailed(); }
          }});
        // a frame that never reports ready (blocked, offline, a stalled
        // embed) is as dead as one that errors
        setTimeout(function(){ if(gen===self.ytGen && !self.ytReady) self.videoFailed(); }, 9000);
      }catch(e){ self.ytWant=false; self.videoFailed(); }
    });
  };
  Deck.prototype.onYtState=function(s){
    // 1 playing, 2 paused, 0 ended - keep the deck's own state honest when
    // the player moves on its own (a track running out, a stalled buffer)
    if(s===1){ if(!this.playing){ this.playing=true; this.el.classList.add("playing"); this.setIcon(true); this.startBtn.setAttribute("aria-pressed","true"); updateLive(); updateGlow(); } }
    else if(s===0 || s===2){ if(this.playing && !this.ytWant){ this.stop(); } }
  };
  /* An embed that will never sound - embedding disabled by the uploader, a
     region block, a frame the browser refused - falls back to whatever clip
     the track has (usually the Apple preview carried along for the BPM), and
     the record forgets the dead video for the rest of the page. */
  Deck.prototype.videoFailed=function(){
    if(this.mode!=="video"||!this.rec) return;
    var rec=this.rec, alt=rec.audioUrl||rec.bpmUrl||null, wasPlaying=this.playing;
    this.unloadYt();
    this.el.classList.remove("has-video");
    if(this.monEl){ this.monEl.classList.remove("on"); updateMonitor(); }
    this.pos=0; this.dur=0; this.peaks=null; this.seekWanted=null; this.drawWave(); this.paintWave(true);
    rec.ytId=null;
    if(alt){
      rec.audioUrl=alt;
      this.mode="buffer";
      if(this.srcTag) this.srcTag.textContent=isApple(alt)?"30s":"clip";
      this.pitchInput.step="0.05"; this.paintPitch();
      this.subEl.textContent=(this._meta||"")+T("loadingPreview"," · loading preview…");
      if(!wasPlaying) this.autoplayWhenReady=false;
      this.loadBuffer(rec);
    } else {
      this.mode="none";
      if(this.srcTag) this.srcTag.textContent="✕";
      if(wasPlaying) this.stop();
      this.subEl.textContent=(this._meta||"")+" · ✕";
    }
  };
  Deck.prototype.applyVolume=function(){
    if(this.chGain&&ac) this.chGain.gain.setTargetAtTime(this.chVol,ac.currentTime,.02);
    if(this.yt&&this.ytReady){ try{ this.yt.setVolume(Math.round(Math.max(0,Math.min(1,this.chVol*MASTER))*100)); }catch(e){} }
  };
  Deck.prototype.loadBuffer=function(rec){
    initAudio(); var self=this, id=++this.loadingId, meta=self._meta||"";
    fetch(rec.audioUrl).then(function(r){ if(!r.ok) throw 0; return r.arrayBuffer(); })
      .then(function(a){ return ac.decodeAudioData(a); })
      .then(function(b){ if(id!==self.loadingId) return;
        self.buffer=b; self.bpmDetected=detectBpm(b); self.updateBpm();
        self.dur=b.duration; self.peaks=computePeaks(b,240); self.drawWave(); self.paintWave(true);
        self.subEl.textContent=meta+T("previewReady"," · preview ready");
        if(self.playing){ self.stop(); self.start(); }
        else if(self.autoplayWhenReady){ self.autoplayWhenReady=false; self.toggle(); } })
      .catch(function(){ if(id!==self.loadingId) return;
        self.buffer=null; self.subEl.textContent=meta; });
  };
  Deck.prototype.toggle=function(){
    if(!this.rec){flashSub(this);return;}
    initAudio(); this.ensureNodes(); if(ac.state==="suspended") ac.resume();
    this.fetchDeferred();
    this.playing?this.stop():this.start();
  };
  Deck.prototype.ensureNodes=function(){
    if(this.chGain) return;
    this.chGain=ac.createGain(); this.chGain.gain.value=this.chVol;
    this.chGain.connect(master); // straight to master bus — you ride the knob to blend
  };
  Deck.prototype.start=function(){
    rtBoothAnnounce();
    this.playing=true; this.el.classList.add("playing");
    this.setIcon(true); this.startBtn.setAttribute("aria-pressed","true");
    if(this.mode==="video"){
      var self=this;
      // YouTube's Required Minimum Functionality: a page must not have more
      // than one player that script has started playing at the same time. So
      // a video deck pauses the OTHER video deck; a clip deck (Web Audio) can
      // still run alongside it, and two clip decks are unaffected.
      var other=(this===deckA)?deckB:deckA;
      if(other&&other.mode==="video"&&other.playing) other.stop();
      this.ensureYt(function(){ if(!self.playing||!self.yt) return;
        try{ self.applyVolume(); self.yt.setPlaybackRate(self.rate()); self.yt.playVideo(); }catch(e){}
        if(self.seekWanted!=null){ try{ self.yt.seekTo(self.seekWanted,true); }catch(e){} self.seekWanted=null; }
        // ready, asked to play, and still "unstarted" seconds later: it is
        // not going to sound (embedding refused, autoplay blocked) - fall back
        var g=self.ytGen;
        setTimeout(function(){
          if(g!==self.ytGen||!self.playing||!self.yt||self.mode!=="video") return;
          var st=-9; try{ st=self.yt.getPlayerState(); }catch(e){}
          if(st===-1||st===5) self.videoFailed();
        }, 6000); });
    } else if(this.buffer){
      this.restartSrc(this.pos);
    } else if(CFG.synthFallback === false){
      // Records mode: never play the groove synth in place of a real
      // preview — the platter spins silently until loadBuffer swaps the
      // decoded clip in (or the record simply has no preview).
    } else {

      this.step=0; this.nextTime=ac.currentTime+0.06; var self=this;
      this.timer=setInterval(function(){self.sched();},25);
    }
    updateLive(); updateGlow();
  };
  Deck.prototype.stop=function(){
    this.autoplayWhenReady=false;
    this.playing=false; this.cuePreview=false; this.el.classList.remove("playing");
    this.setIcon(false); this.startBtn.setAttribute("aria-pressed","false");
    if(this.src){ try{this.src.stop();}catch(e){} this.src.disconnect(); this.src=null; }
    if(this.timer){ clearInterval(this.timer); this.timer=null; }
    this.ytWant=false;
    if(this.yt&&this.ytReady){ try{ this.yt.pauseVideo(); }catch(e){} }
    updateLive(); updateGlow();
  };
  /* ---- Transport mechanics: rate, restart, nudge, cue ---- */
  Deck.prototype.rate=function(){return this.speed*(1+(this.pitch+this.bend)/100);};
  Deck.prototype.applyRate=function(){
    if(this.src&&ac) this.src.playbackRate.setTargetAtTime(this.rate(),ac.currentTime,.02);
    if(this.mode==="video"&&this.yt&&this.ytReady){ try{ this.yt.setPlaybackRate(this.rate()); }catch(e){} }
    if(this.mode==="video") this.paintPitch();
  };
  // (Re)start the source from `offset` seconds - the top when omitted.
  Deck.prototype.restartSrc=function(offset){
    var at=(typeof offset==="number"&&offset>0)?offset:0;
    if(this.mode==="video"){
      this.pos=at;
      if(this.yt&&this.ytReady){ try{ this.yt.seekTo(at,true); this.yt.playVideo(); }catch(e){} }
      else this.seekWanted=at;
      this.paintWave(true);
      return;
    }
    if(!this.buffer) return;
    if(at>=this.buffer.duration) at=0;
    if(this.src){ try{this.src.stop();}catch(e){} this.src.disconnect(); this.src=null; }
    this.src=ac.createBufferSource(); this.src.buffer=this.buffer; this.src.loop=true;
    this.src.playbackRate.value=this.rate();
    this.src.connect(this.chGain); this.src.start(0,at);
    this.pos=at; this.paintWave(true);
  };
  Deck.prototype.seek=function(sec){
    var total=this.dur||this.durHint; if(!(total>0)) return;
    sec=Math.max(0,Math.min(total,sec));
    if(this.mode==="video"){
      this.pos=sec;
      if(this.yt&&this.ytReady){ try{ this.yt.seekTo(sec,true); }catch(e){} }
      else this.seekWanted=sec;
    } else if(this.buffer){
      if(this.playing) this.restartSrc(sec); else this.pos=sec;
    }
    this.paintWave(true);
  };
  /* The strip under each deck: the decoded clip's real waveform where there
     is one, a plain minute ruler where there is not (a video deck - its
     samples never reach the page), plus the playhead and the two times. The
     canvas is only repainted on load or resize; per frame it is two style
     writes and, four times a second, two text nodes. */
  Deck.prototype.drawWave=function(){
    var c=this.waveC; if(!c||!this.waveEl) return;
    var w=this.waveEl.clientWidth||160, h=this.waveEl.clientHeight||20, dpr=window.devicePixelRatio||1;
    var cw=Math.round(w*dpr), ch=Math.round(h*dpr);
    if(c.width!==cw||c.height!==ch){ c.width=cw; c.height=ch; }
    this._waveW=w;
    var g=c.getContext("2d"); g.clearRect(0,0,cw,ch); g.save(); g.scale(dpr,dpr);
    var col=this.side==="a"?"#e8a020":"#38c6e4";
    if(this.peaks){
      var n=this.peaks.length, bw=w/n, mid=h/2;
      g.fillStyle=col; g.globalAlpha=.85;
      for(var i=0;i<n;i++){ var ph=Math.max(1,this.peaks[i]*(h-2)); g.fillRect(i*bw, mid-ph/2, Math.max(1,bw-0.5), ph); }
    } else {
      var total=this.dur||this.durHint;
      g.strokeStyle="rgba(255,255,255,.22)"; g.lineWidth=1;
      g.beginPath(); g.moveTo(0,Math.floor(h/2)+.5); g.lineTo(w,Math.floor(h/2)+.5); g.stroke();
      if(total>60){
        g.strokeStyle="rgba(255,255,255,.4)";
        for(var t=60;t<total;t+=60){ var x=Math.round(t/total*w)+.5; g.beginPath(); g.moveTo(x,h/2-3); g.lineTo(x,h/2+3); g.stroke(); }
      }
    }
    g.restore();
  };
  Deck.prototype.paintWave=function(force){
    if(!this.waveEl) return;
    var total=this.dur||this.durHint, frac=total?Math.max(0,Math.min(1,this.pos/total)):0;
    var pct=(frac*100).toFixed(2)+"%";
    this.wavePh.style.left=pct; this.waveFill.style.width=pct;
    var now=performance.now();
    if(force||now-this._waveT>250){
      this._waveT=now;
      this.waveL.textContent=fmtTime(this.pos);
      this.waveR.textContent=total?fmtTime(total):"–:––";
      this.waveEl.setAttribute("aria-valuenow",Math.round(frac*100));
      var cw=this.waveEl.clientWidth;
      if(cw&&cw!==this._waveW) this.drawWave();
    }
  };
  // Momentary pitch bend — hold to push/drag the record against the other deck.
  Deck.prototype.nudge=function(dir,on){
    if(!this.rec) return;
    // a video deck only hears whole steps, so its push has to be a whole step
    this.bend=on?dir*(this.mode==="video"&&YT_RATE_STEP>0?YT_RATE_STEP*100:3):0;
    this.applyRate();
  };
  // CDJ-style cue: playing → stutter back to the top of the loop and keep going;
  // stopped → preview from the top while held (release stops).
  Deck.prototype.cueDown=function(){
    if(!this.rec){ flashSub(this); return; }
    initAudio(); this.ensureNodes(); if(ac.state==="suspended") ac.resume();
    this.fetchDeferred();
    if(this.playing){
      if(this.buffer||this.mode==="video"){ this.restartSrc(0); }
      else if(CFG.synthFallback !== false){ this.step=0; this.nextTime=ac.currentTime+0.02; }
    } else {
      this.pos=0; if(this.mode==="video") this.seekWanted=0;
      this.cuePreview=true; this.start();
    }
  };
  Deck.prototype.cueUp=function(){
    if(this.cuePreview){ this.cuePreview=false; this.stop(); }
  };
  Deck.prototype.sched=function(){
    var r=this.rec; if(!r) return; var sp=(60/this.effBpm())/4;
    while(this.nextTime<ac.currentTime+0.1){
      var s=this.step, t=this.nextTime;
      if(r.swing&&(s%2===1)) t+=sp*r.swing;
      if(r.kick[s]){ kick(t,this.chGain); this.pulse(t); }
      if(r.snare[s]) snare(t,this.chGain);
      if(r.hat[s]) hat(t,this.chGain,r.hat[s]===2);
      if(r.bass[s]) bassHit(t,r.bass[s],this.chGain,r,sp);
      this.nextTime+=sp; this.step=(this.step+1)%16;
    }
  };
  Deck.prototype.pulse=function(t){
    var delay=Math.max(0,(t-ac.currentTime)*1000);
    setTimeout(function(){var l=document.getElementById("rt-live");
      l.classList.add("on"); setTimeout(function(){l.classList.remove("on");},80);},delay);
  };
  Deck.prototype.draw=function(){
    var c=this.ctx, W=380;
    c.clearRect(0,0,W,W); c.save(); c.translate(190,190);
    c.beginPath(); c.arc(0,0,186,0,Math.PI*2);
    var pg=c.createRadialGradient(-40,-40,20,0,0,186);
    pg.addColorStop(0,"#2a2a2e"); pg.addColorStop(1,"#161618"); c.fillStyle=pg; c.fill();
    c.save(); c.rotate(this.angle);
    for(var i=0;i<44;i++){ c.save(); c.rotate((i/44)*Math.PI*2);
      c.fillStyle=i%2?"rgba(255,255,255,.10)":"rgba(255,255,255,.22)";
      c.fillRect(174,-1.4,7,2.8); c.restore(); }
    c.strokeStyle="rgba(255,255,255,.035)"; c.lineWidth=1;
    for(var r=70;r<168;r+=7){ c.beginPath(); c.arc(0,0,r,0,Math.PI*2); c.stroke(); }
    // An empty deck used to get a near-black label with nothing on it. The
    // label belongs to the booth, not to whatever happens to be loaded, so the
    // wordmark is always there; idle just gets a blank-pressing grey light
    // enough for a dark mark to read on.
    // One label for every record. rec.lbl was a colour hashed out of the
    // groove table - the lime green on a house 12" meant nothing. A blank
    // pressing reads as a record and lets the mark carry the identity.
    var lbl="#d9d4c8";
    c.beginPath(); c.arc(0,0,60,0,Math.PI*2); c.fillStyle=lbl; c.fill();

    if(LOGO && LOGO.complete && LOGO.naturalWidth){
      // the icon is square, so size it off the label rather than the mark's
      // own aspect: 54px inside a 120px label leaves a proper margin
      var lw=54, lh=lw*(LOGO.naturalHeight/LOGO.naturalWidth);
      // centred when idle; lifted to make room for the BPM once loaded
      c.globalAlpha=this.rec?.72:.82;
      c.drawImage(LOGO, -lw/2, -lh/2+(this.rec?-9:0), lw, lh);
      c.globalAlpha=1;
    } else if(this.rec){
      // only until the wordmark decodes, or if the theme has no logo set
      c.fillStyle="rgba(0,0,0,.5)"; c.fillRect(-16,-26,11,11);
      c.beginPath(); c.moveTo(6,-15); c.lineTo(17,-15); c.lineTo(11.5,-26); c.closePath(); c.fill();
    }
    if(this.rec){
      c.fillStyle="rgba(0,0,0,.55)"; c.textAlign="center"; c.font="600 12px IBM Plex Mono, monospace";
      var shown=this.baseBpm();
      c.fillText(shown?shown.toFixed(1)+" BPM":"RT",0,20);
    }
    c.restore();
    c.beginPath(); c.arc(0,0,4.5,0,Math.PI*2); c.fillStyle="#0c0c0e"; c.fill();
    c.beginPath(); c.arc(0,0,2,0,Math.PI*2); c.fillStyle="#3a3a3e"; c.fill();
    c.restore();
  };

  /* ================= MIXER: rotary levels + booth glow ================= */
  // On a rotary mixer there's no crossfader — the blend IS the two channel
  // knobs. Booth lighting tracks each channel's live level.
  function updateGlow(){
    var a=deckA.playing?deckA.chVol:0, b=deckB.playing?deckB.chVol:0;
    document.documentElement.style.setProperty("--glowA",(0.10+a*0.70).toFixed(3));
    document.documentElement.style.setProperty("--glowB",(0.10+b*0.70).toFixed(3));
  }

  // Reusable rotary knob: pointer-drag (vertical), wheel, and arrow keys. 270° sweep.
  function makeKnob(opts){
    var min=opts.min, max=opts.max, val=opts.value, step=opts.step||1;
    var el=document.createElement("div");
    el.className="rt-knob"+(opts.cls?(" "+opts.cls):"");
    el.tabIndex=0; el.setAttribute("role","slider");
    el.setAttribute("aria-label",opts.label);
    el.setAttribute("aria-valuemin",min); el.setAttribute("aria-valuemax",max);
    el.innerHTML='<div class="rt-knob-dial"><span class="rt-knob-ptr"></span></div>'+
      '<div class="rt-knob-cap">'+opts.label+(opts.sub?'<small>'+opts.sub+'</small>':'')+'</div>';
    var dial=el.querySelector(".rt-knob-dial");
    function render(){
      var frac=(val-min)/(max-min);
      dial.style.transform="rotate("+(frac*270-135)+"deg)";
      el.setAttribute("aria-valuenow",Math.round(val));
    }
    function set(v,fire){ val=Math.max(min,Math.min(max,v)); render(); if(fire!==false) opts.onInput(val); }
    render();
    var startY=0, startV=0, dragging=false;
    el.addEventListener("pointerdown",function(e){ dragging=true; startY=e.clientY; startV=val;
      el.setPointerCapture(e.pointerId); e.preventDefault(); });
    el.addEventListener("pointermove",function(e){ if(!dragging) return;
      set(startV+(startY-e.clientY)*(max-min)/180); });
    el.addEventListener("pointerup",function(e){ dragging=false; try{el.releasePointerCapture(e.pointerId);}catch(x){} });
    el.addEventListener("wheel",function(e){ e.preventDefault(); set(val-Math.sign(e.deltaY)*step*2); },{passive:false});
    el.addEventListener("keydown",function(e){ var k=e.key;
      if(k==="ArrowUp"||k==="ArrowRight"){ set(val+step); e.preventDefault(); }
      else if(k==="ArrowDown"||k==="ArrowLeft"){ set(val-step); e.preventDefault(); }
      else if(k==="Home"){ set(min); e.preventDefault(); }
      else if(k==="End"){ set(max); e.preventDefault(); } });
    return { el:el, set:set, get:function(){return val;} };
  }
  function updateLive(){
    var t=document.getElementById("rt-live-txt"), l=document.getElementById("rt-live");
    var on=deckA.playing||deckB.playing; t.textContent=on?T("live","booth live"):T("idle","booth idle");
    // The status line only earns its space when a deck is actually running.
    // Note this is a separate class from the dot's `on`, which is an 80ms
    // per-beat pulse - keying the strip off that would make it strobe.
    var wrap=document.querySelector(".rt-wrap");
    if(wrap) wrap.classList.toggle("rt-playing", on);
    [deckA,deckB].forEach(function(d){ if(d&&d.monEl) d.monEl.classList.toggle("playing", !!d.playing); });
    if(!on) l.classList.remove("on");
  }
  function flashSub(d){var el=d.subEl,o=el.textContent;el.textContent=T("loadFirst","▸ load a record first");
    setTimeout(function(){el.textContent=o;},1100);}

  /* ================= BUILD ================= */
  var deckA=new Deck("a"), deckB=new Deck("b");

  var mixer=document.createElement("section"); mixer.className="rt-mixer";
  mixer.innerHTML=
    '<span class="cheek l"></span><span class="cheek r"></span>'+
    '<span class="screw tl"></span><span class="screw tr"></span><span class="screw bl"></span><span class="screw br"></span>'+
    '<div class="rt-face-title">rtm-20</div><div class="rt-face-sub">rotary mixer</div>'+
    '<div class="rt-vu-row">'+
      '<div class="rt-vu a"><div class="rt-vu-face"><div class="rt-vu-scale"></div><div class="rt-vu-needle"></div></div><span class="rt-vu-lbl">A</span></div>'+
      '<div class="rt-vu b"><div class="rt-vu-face"><div class="rt-vu-scale"></div><div class="rt-vu-needle"></div></div><span class="rt-vu-lbl">B</span></div>'+
    '</div>';

  var rowCh=document.createElement("div"); rowCh.className="rt-knob-row";
  var divi=document.createElement("div"); divi.className="rt-face-div";
  var rowEq=document.createElement("div"); rowEq.className="rt-knob-row";
  var rowMaster=document.createElement("div"); rowMaster.className="rt-knob-row";

  var knobA=makeKnob({label:"Ch A",cls:"a big",min:0,max:100,value:80,step:2,onInput:function(v){
    deckA.chVol=v/100; deckA.applyVolume(); updateGlow(); }});
  var knobB=makeKnob({label:"Ch B",cls:"b big",min:0,max:100,value:80,step:2,onInput:function(v){
    deckB.chVol=v/100; deckB.applyVolume(); updateGlow(); }});
  deckA.chVol=0.8; deckB.chVol=0.8;

  var knobBass=makeKnob({label:"Bass",min:-12,max:12,value:0,step:1,onInput:function(v){
    if(eqBass) eqBass.gain.setTargetAtTime(v,ac.currentTime,.02); }});
  var knobTreble=makeKnob({label:"Treble",min:-12,max:12,value:0,step:1,onInput:function(v){
    if(eqTreble) eqTreble.gain.setTargetAtTime(v,ac.currentTime,.02); }});
  var knobMaster=makeKnob({label:"Master",min:0,max:100,value:85,step:2,onInput:function(v){
    MASTER=v/100;
    if(master) master.gain.setTargetAtTime(v/100,ac.currentTime,.02);
    deckA.applyVolume(); deckB.applyVolume(); }});

  rowCh.appendChild(knobA.el); rowCh.appendChild(knobB.el);
  rowEq.appendChild(knobBass.el); rowEq.appendChild(knobTreble.el);
  rowMaster.appendChild(knobMaster.el);
  mixer.appendChild(rowCh); mixer.appendChild(divi); mixer.appendChild(rowEq); mixer.appendChild(rowMaster);

  var syncBtn=document.createElement("button"); syncBtn.className="rt-sync"; syncBtn.textContent="Sync B → A";
  mixer.appendChild(syncBtn);
  document.getElementById("rt-console").insertBefore(mixer, deckB.el);

  // Twin VU: needles ride each channel's playing level, with a little program bounce.
  var _vuA=mixer.querySelector(".rt-vu.a .rt-vu-needle"), _vuB=mixer.querySelector(".rt-vu.b .rt-vu-needle");
  setInterval(function(){
    [[_vuA,deckA],[_vuB,deckB]].forEach(function(p){
      var lvl=p[1].playing?p[1].chVol*(0.55+Math.random()*0.45):0;
      p[0].style.transform="rotate("+(-42+Math.min(1,lvl)*76).toFixed(1)+"deg)";
    });
  },130);

  syncBtn.addEventListener("click",function(){
    if(!deckA.rec||!deckB.rec) return;
    if(!deckA.baseBpm() || !deckB.baseBpm()) return;   // nothing real to match
    var p=(deckA.effBpm()/deckB.baseBpm()-1)*100;
    // If ±8 can't reach the target tempo, flip deck B to ±16 — never sync short.
    if(Math.abs(p)>deckB.range&&deckB.range===8){
      deckB.range=16; deckB.rangeBtn.textContent="±16";
      deckB.rangeBtn.setAttribute("aria-label","Pitch range deck B, currently plus or minus 16 percent");
      deckB.pitchInput.min=-16; deckB.pitchInput.max=16;
    }
    p=Math.max(-deckB.range,Math.min(deckB.range,p));
    // a video deck lands on the nearest step the player will honour
    if(deckB.mode==="video"&&YT_RATE_STEP>0) p=Math.round(p/(YT_RATE_STEP*100))*(YT_RATE_STEP*100);
    deckB.pitch=p; deckB.pitchInput.value=p.toFixed(1);
    deckB.paintPitch();
    deckB.strobe.classList.toggle("lock",Math.abs(p)<0.05); deckB.updateBpm();
    deckB.applyRate();
    // Synth decks share one clock — drop B onto A's grid so the kicks land together.
    if(!deckA.buffer&&!deckB.buffer&&deckA.playing&&deckB.playing){
      deckB.step=deckA.step; deckB.nextTime=deckA.nextTime;
    }
  });

  var crate=document.getElementById("rt-records");
  function crateMsg(t){ crate.innerHTML='<div class="rt-crate-msg">'+t+'</div>'; }
  function renderCrate(items){
    crate.innerHTML="";
    items.forEach(function(rec){
      var wrap=document.createElement("div"); wrap.className="rt-recwrap";
      var card=document.createElement("div"); card.className="rt-rec"+(rec.available?"":" sold"); card.style.setProperty("--lbl",rec.lbl);
      var cover=rec.image?'<img class="rt-rec-cover" src="'+esc(rec.image)+'" alt="" loading="lazy" width="400" height="400">':'<div class="rt-rec-disc"></div>';
      card.innerHTML=cover+'<div class="rt-rec-meta"><div class="rt-rec-name">'+esc(rec.title)+'</div><div class="rt-rec-sub">'+esc(rec.artist)+(rec.price?(" · "+rec.price):"")+'</div></div>';
      var load=document.createElement("div"); load.className="rt-load";
      var ba=document.createElement("button"); ba.className="a"; ba.textContent="▸ A";
      var bb=document.createElement("button"); bb.className="b"; bb.textContent="▸ B";
      ba.setAttribute("aria-label",T("loadDeckA","Load to deck A")); bb.setAttribute("aria-label",T("loadDeckB","Load to deck B"));
      ba.addEventListener("click",function(){deckA.load(rec);});
      bb.addEventListener("click",function(){deckB.load(rec);});
      load.appendChild(ba); load.appendChild(bb);
      wrap.appendChild(card); wrap.appendChild(load); crate.appendChild(wrap);
    });
  }
  var CATALOG=[];
  function finishCatalog(items){
    // Buyable records first — the decks should never default to a sold-out sleeve.
    items.sort(function(x,y){ return (y.available?1:0)-(x.available?1:0); });
    CATALOG=items;
    if(!items.length){ crateMsg(T("emptyCrate","No records with previews available yet.")); return; }
    renderCrate(items);
    // Records mode is desktop-only (the section is display:none under 750px).
    // Auto-loading there would fetch and decode two preview MP3s for a booth
    // nobody can see — on cellular, for nothing.
    if(NARROW_RECORDS) return;
    if(items[0]) deckA.load(items[0],{lazy:true});
    deckB.load(items[1]||items[0],{lazy:true});
  }
  // Only records carrying a licensed preview (custom.yy_audios) are eligible.
  // The feed is paginated; scan pages until the crate is filled or we run out.
  function fetchCatalog(){
    if (CFG.inline) {
      // Records mode: the crate is rendered into the page by
      // snippets/rt-booth-data.liquid — no feed request at all.
      var dataEl = document.getElementById("rt-booth-data");
      var inlineItems = [];
      if (dataEl) { try { inlineItems = (JSON.parse(dataEl.textContent)||{}).items || []; } catch(e){ inlineItems = []; } }
      finishCatalog(inlineItems.map(mapFeedItem).filter(function(x){ return x.title && (x.audioUrl || x.ytId); }).slice(0,48));
      return;
    }
    crateMsg(T("loadingCrate","Loading the Yoyaku crate…"));
    var acc=[], MAX_PAGES=8, CAP=48;
    function page(p){
      fetch(STORE.base+STORE.feed+"?page="+p)
        .then(function(r){ if(!r.ok) throw 0; return r.json(); })
        .then(function(d){
          var list = Array.isArray(d) ? d : (d.items||[]);
          acc = acc.concat(list.map(mapFeedItem).filter(function(x){ return x.title && (x.audioUrl || x.ytId); }));
          var more = d && d.next;
          if(more && p<MAX_PAGES && acc.length<CAP) page(p+1);
          else finishCatalog(acc.slice(0,CAP));
        })
        .catch(function(){
          if(acc.length) finishCatalog(acc.slice(0,CAP));
          else fallbackProducts();
        });
    }
    page(1);
  }
  // Fallback: the Storefront products.json endpoint works on any theme
  // (preview or live) but carries no preview audio, so these records play the

  // built-in groove synth instead of a real clip.
  function fallbackProducts(){
    crateMsg(T("loadingCrate","Loading the Yoyaku crate…"));
    fetch(STORE.base+"/collections/"+STORE.collection+"/products.json?limit=48",{headers:{Accept:"application/json"}})
      .then(function(r){ if(!r.ok) throw 0; return r.json(); })
      .then(function(d){
        var items=(d.products||[]).map(mapProduct).filter(function(x){ return x.title; });
        if(items.length) finishCatalog(items.slice(0,48));
        else crateMsg("No Yoyaku records available right now.");
      })
      .catch(function(){ crateMsg("Open this on rectangletriangle.com to load the Yoyaku crate."); });
  }
  fetchCatalog();

  /* ===== Public API: the record wall + mini transport drive the decks ===== */
  window.RTBooth={
    decks:{a:deckA,b:deckB},
    loadData:function(side,it){
      var rec=null, h=(it&&it.handle)||"";
      // Records mode puts ONE CRATE ENTRY PER TRACK, all sharing the product
      // handle, so a handle-only match always returns track 1. Match the
      // clicked clip first: that entry carries the right title AND the right
      // audio. Handle matching stays as the fallback (yoyaku mode, where a
      // card's mp3 may differ from the feed's).
      var vid=ytIdFrom(it&&it.yt);
      if(vid){ for(var vi=0;vi<CATALOG.length;vi++){ if(CATALOG[vi].ytId===vid){ rec=CATALOG[vi]; break; } } }
      if(!rec&&it&&it.audio){ for(var ai=0;ai<CATALOG.length;ai++){ if(CATALOG[ai].audioUrl===it.audio){ rec=CATALOG[ai]; break; } } }
      if(!rec&&h){ for(var i=0;i<CATALOG.length;i++){ var u=CATALOG[i].url||""; if(u.split("/products/")[1]===h){ rec=CATALOG[i]; break; } } }
      if(!rec) rec=mapFeedItem(it||{});
      // Card data wins where the catalog is thinner: real artist line, the
      // card's preview MP3 when the catalog entry carries no audio, and the
      // card's video id / BPM clip when the entry has none.
      if(it&&((it.artist&&rec.artist!==it.artist)||(it.audio&&!rec.audioUrl)||(vid&&!rec.ytId)||(it.bpm&&!rec.bpmUrl))){
        var m={},k; for(k in rec) m[k]=rec[k];
        if(it.artist) m.artist=it.artist;
        if(it.audio&&!m.audioUrl) m.audioUrl=it.audio;
        if(vid&&!m.ytId) m.ytId=vid;
        if(it.bpm&&!m.bpmUrl) m.bpmUrl=it.bpm;
        rec=m;
      }
      ((side==="b")?deckB:deckA).load(rec);
      var t=document.getElementById("rt-live-txt");
      if(t){ t.textContent=T("deckLoaded","deck {X} loaded").replace("{X}",side==="b"?"B":"A"); setTimeout(updateLive,1400); }
      return rec;
    },
    /* Load AND start: the one call every play trigger on the page makes.
       side null = pick a deck (empty first, then the one not playing). A
       track already sitting on a deck is toggled rather than reloaded, so a
       second click on the same card or row pauses it, the way the sidebar
       player always behaved. */
    play:function(side,it){
      var a=deckA, b=deckB, vid=ytIdFrom(it&&it.yt), au=(it&&it.audio)||"";
      var held=null;
      [a,b].forEach(function(d){
        if(held||!d.rec) return;
        if((vid&&d.rec.ytId===vid)||(au&&d.rec.audioUrl===au)) held=d;
      });
      if(held&&(!side||held.side===side)){ held.toggle(); return held.rec; }
      if(!side) side = !a.rec ? "a" : (!b.rec ? "b" : (!a.playing ? "a" : (!b.playing ? "b" : "a")));
      var rec=this.loadData(side,it);
      var d=(side==="b")?b:a;
      if(d.mode==="video"||d.buffer){ if(!d.playing) d.toggle(); }
      else d.autoplayWhenReady=true;
      return rec;
    },
    ytIdFrom:ytIdFrom,
    catalog:function(){ return CATALOG.slice(); },
    narrow:NARROW_RECORDS
  };
  document.dispatchEvent(new Event("rt:booth-ready"));

  var storeLink=document.getElementById("rt-store-link");
  if(storeLink) storeLink.href=STORE.base+"/collections/"+STORE.collection;
  // Footer links open in a new tab so clicking one never tears down a live mix
  // (Shopify richtext settings strip target=, so it is set here).
  var footLinks=document.querySelectorAll(".rt-foot a");
  for(var fi=0;fi<footLinks.length;fi++){ footLinks[fi].target="_blank"; footLinks[fi].rel="noopener"; }

  var reduce=window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var last=performance.now();
  var wrapEl=document.querySelector(".rt-wrap");
  function frame(now){
    var dt=(now-last)/1000; last=now;
    // On a phone the rail is display:none, so drawing both platters every
    // frame burns battery for something nobody can see. offsetParent is null
    // whenever an ancestor is hidden; one property read, and it resumes by
    // itself if the window is widened.
    var visible = !wrapEl || wrapEl.offsetParent !== null;
    [deckA,deckB].forEach(function(d){
      var target=d.playing?3.49*d.actualRate():0;
      d.vel+=(target-d.vel)*Math.min(1,dt*3.5);
      if(!reduce) d.angle+=d.vel*dt;
      // the needle's position: integrated for a Web Audio source (the node
      // has no clock of its own), read back from the player for a video
      if(d.playing){
        if(d.mode==="buffer"&&d.buffer){ d.pos+=dt*d.actualRate(); if(d.dur&&d.pos>=d.dur) d.pos-=d.dur; }
        else if(d.mode==="video"&&d.yt&&d.ytReady){
          try{ d.pos=d.yt.getCurrentTime()||0; var yd=d.yt.getDuration(); if(yd>0) d.dur=yd; }catch(e){}
        }
      }
      if(visible){ d.draw(); d.paintWave(false); }
    });
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
  updateGlow();
})();

/* ---- record-wall ⇄ booth bridge (was snippets/rt-mixer-b10) ----
   Puts "load to deck" buttons on grid product cards via the window.RTBooth
   API. The card's preview clip now comes from the play overlay's
   data-play-url attribute — the old version scraped it out of the inline
   onclick attribute with a regex, which broke whenever that attribute
   changed shape. In records mode (CFG.routeCardPlays) the booth also
   becomes the page's player: card overlays and tracklist play buttons
   load a deck instead of the sidebar player. */
(function(){
  "use strict";
  var cfgEl=document.getElementById("rt-booth-config");
  var CFG={};
  if(cfgEl){ try{ CFG=JSON.parse(cfgEl.textContent)||{}; }catch(e){ CFG={}; } }
  var CARD_SEL=(CFG.gridSelector||'[id$="__yoyaku_grid"]')+" .card-wrapper";

  function cardData(cw){
    var a=cw.querySelector('a[href*="/products/"]'); if(!a) return null;
    var handle=(a.getAttribute("href").split("/products/")[1]||"").split("?")[0].split("#")[0];
    var h=cw.querySelector(".card__heading");
    var art=cw.querySelector(".card-artist-link");
    var pr=cw.querySelector(".card-price");
    var img=cw.querySelector(".card__media img")||cw.querySelector("img");
    var audio="", yt="";
    var pb=cw.querySelector(".card-play-button-overlay[data-play-url]");
    if(pb){ audio=pb.getAttribute("data-play-url")||""; yt=pb.getAttribute("data-play-yt")||""; }
    // A YouTube watch URL in the play slot (the old video_string fallback) is
    // a video id in disguise: WebAudio can't decode it, the video deck can.
    var idFrom=window.RTBooth&&window.RTBooth.ytIdFrom;
    if(idFrom&&/youtube\.com|youtu\.be/i.test(audio)){ yt=yt||idFrom(audio); audio=""; }
    return { handle:handle,
             title:h?h.textContent.trim():handle,
             artist:art?art.textContent.trim():"",
             price:pr?pr.textContent.trim():"",
             image:img?(img.currentSrc||img.src):"",
             audio:audio, yt:yt };
  }
  function decorate(){
    if(!window.RTBooth) return;
    // Below 960px the rail is hidden, so a "load to deck" button would drive a
    // booth nobody can see. Don't put them on the card at all down there.
    if(window.innerWidth < 960) return;
    document.querySelectorAll(CARD_SEL).forEach(function(cw){
      if(cw.querySelector(".rt-grid-load")) return;
      var data=cardData(cw);
      if(!data) return;
      if(CFG.requirePreview&&!data.audio&&!data.yt) return;
      var row=document.createElement("div"); row.className="rt-grid-load";
      var LBL = CFG.i18n || {};
      [["a", LBL.deckABtn || "▸ deck A"],["b", LBL.deckBBtn || "▸ deck B"]].forEach(function(cfg){
        var b=document.createElement("button"); b.type="button"; b.className=cfg[0]; b.textContent=cfg[1];
        b.addEventListener("click",function(e){
          e.preventDefault(); e.stopPropagation();
          window.RTBooth.loadData(cfg[0],cardData(cw));
          var o=b.textContent; b.textContent=(LBL.loaded || "loaded ✓"); b.classList.add("done");
          setTimeout(function(){ b.textContent=o; b.classList.remove("done"); },1200);
        });
        row.appendChild(b);
      });
      var host=cw.querySelector(".card__inner")||cw.querySelector(".card__content")||cw;
      host.appendChild(row);
    });
    syncCrateVisibility();
  }
  // The crate is for pages that cannot load a deck any other way. Wherever the
  // grid gives cards their own A/B buttons - the homepage included - those are
  // the way to pick, so the crate stands down rather than offering a second,
  // narrower list of the same thing.
  //
  // Decided from the page rather than from a list of templates: if the cards
  // are there but none of them carries a preview, nothing can be loaded from
  // them and the crate is still the only way in, so it stays.
  // Only ever called from decorate(), which is the one place that knows
  // whether the cards carry buttons. It deliberately does NOT hang off the
  // feed: what the feed returns has no bearing on the decision, and reaching
  // for it from the engine's scope is what broke the deck auto-load once.
  function syncCrateVisibility(){
    var wrap=document.querySelector(".rt-wrap.rt-rail");
    if(!wrap||CFG.inline) return;      // product/collection already hide it in Liquid
    wrap.classList.toggle("rt-no-crate", !!document.querySelector(".rt-grid-load"));
  }
  // Related products (and any other async grid) arrive after DOMContentLoaded;
  // watch for them instead of only decorating once.
  var mo=null;
  function arm(){
    if(mo||!("MutationObserver" in window)) return;
    var root=document.getElementById("MainContent")||document.body;
    var t=null;
    mo=new MutationObserver(function(){ clearTimeout(t); t=setTimeout(decorate,250); });
    mo.observe(root,{childList:true,subtree:true});
  }
  function init(){ decorate(); arm(); }
  if(document.readyState!=="loading") init();
  else document.addEventListener("DOMContentLoaded",init);
  document.addEventListener("rt:booth-ready",decorate);
  // rt-softnav swaps the whole content column: re-bind the observer to the new #MainContent and decorate
  // the new cards (the ▸A/▸B buttons were missing on every swapped-in page).
  document.addEventListener("rt:page-swapped",function(){ if(mo){ mo.disconnect(); mo=null; } arm(); decorate(); });

  /* Records mode: intercept every play trigger in capture phase so the
     sitewide delegated listener (layout/theme.liquid) never sees the click,
     and route it onto a deck. */
  if (CFG.routeCardPlays) {
    document.addEventListener("click", function(ev){
      if(!window.RTBooth||!window.RTBooth.decks) return;
      var btn = ev.target.closest ? ev.target.closest(".card-play-button-overlay, .rt-play-trigger") : null;
      if (!btn) return;
      // Mobile keeps the simple player bar: the booth section is display:none
      // under 750px in records mode, so never hijack taps into hidden decks.
      if (window.innerWidth < 960) return;
      var it = null;
      var cw = btn.closest(".card-wrapper");
      if (cw) it = cardData(cw);
      if (!it || (!it.audio && !it.yt)) {
        var handle = ((btn.getAttribute("data-play-fetch")||"").split("/products/")[1]||"").split("?")[0];
        if (!handle) handle = ((location.pathname.split("/products/")[1]||"").split("/")[0]||"").split("?")[0];
        it = { handle: handle,
               title: btn.getAttribute("data-play-title")||"",
               artist: btn.getAttribute("data-play-artist")||"",
               image: btn.getAttribute("data-play-image")||"",
               price: "",
               audio: btn.getAttribute("data-play-url")||"",
               yt: btn.getAttribute("data-play-yt")||"" };
      }
      // A YouTube watch URL in the play slot is a video id in disguise.
      if (/youtube\.com|youtu\.be/i.test(it.audio)) { it.yt = it.yt || window.RTBooth.ytIdFrom(it.audio); it.audio = ""; }
      // Nothing a deck can hold: leave the click alone so the sitewide
      // listener handles it as before.
      if (!it.audio && !it.yt) return;
      ev.preventDefault(); ev.stopPropagation(); ev.stopImmediatePropagation();
      window.RTBooth.play(null, it);
    }, true);
  }
})();

/* ---- booth ergonomics: sticky mini transport (was snippets/rt-mixer-b11) ---- */
(function(){
  "use strict";
  function init(){
    if(!window.RTBooth||!window.RTBooth.decks) return;
    // Records mode on mobile hides the booth entirely; a floating transport
    // driving invisible decks would be a control with no context.
    if(window.RTBooth.narrow) return;
    var booth=document.querySelector(".rt-booth");
    if(!booth||document.getElementById("rt-transport")) return;
    var t=document.createElement("div"); t.id="rt-transport";
    function cluster(side){
      var d=window.RTBooth.decks[side];
      var c=document.createElement("div"); c.className="rt-tp-deck "+side;
      c.innerHTML='<span class="rt-tp-tag">'+side.toUpperCase()+'</span>'+
        '<button class="rt-tp-play" aria-label="Play or pause deck '+side.toUpperCase()+'">▶</button>'+
        '<button class="rt-tp-cue" aria-label="Cue deck '+side.toUpperCase()+'">CUE</button>'+
        '<button class="rt-tp-n" data-n="-1" aria-label="Nudge deck '+side.toUpperCase()+' slower">◂</button>'+
        '<button class="rt-tp-n" data-n="1" aria-label="Nudge deck '+side.toUpperCase()+' faster">▸</button>'+
        '<input class="rt-tp-pitch" type="range" min="-8" max="8" step="0.05" value="0" aria-label="Pitch deck '+side.toUpperCase()+'">'+
        '<span class="rt-tp-bpm">—</span>'+
        '<span class="rt-tp-name">no record</span>';
      c.querySelector(".rt-tp-play").addEventListener("click",function(){ d.toggle(); });
      var cue=c.querySelector(".rt-tp-cue");
      cue.addEventListener("pointerdown",function(e){ e.preventDefault(); d.cueDown(); });
      cue.addEventListener("pointerup",function(){ d.cueUp(); });
      cue.addEventListener("pointercancel",function(){ d.cueUp(); });
      cue.addEventListener("pointerleave",function(){ d.cueUp(); });
      var ns=c.querySelectorAll(".rt-tp-n");
      for(var i=0;i<ns.length;i++)(function(nb){
        var dir=parseFloat(nb.getAttribute("data-n"));
        nb.addEventListener("pointerdown",function(e){ e.preventDefault(); d.nudge(dir,true); });
        nb.addEventListener("pointerup",function(){ d.nudge(dir,false); });
        nb.addEventListener("pointercancel",function(){ d.nudge(dir,false); });
        nb.addEventListener("pointerleave",function(){ if(d.bend!==0) d.nudge(dir,false); });
      })(ns[i]);
      var pi=c.querySelector(".rt-tp-pitch");
      pi.addEventListener("input",function(){
        d.pitchInput.value=this.value;
        var ev=document.createEvent("Event"); ev.initEvent("input",true,true);
        d.pitchInput.dispatchEvent(ev);
      });
      c._els={play:c.querySelector(".rt-tp-play"),pitch:pi,bpm:c.querySelector(".rt-tp-bpm"),name:c.querySelector(".rt-tp-name"),deck:d};
      return c;
    }
    var ca=cluster("a"), cb=cluster("b");
    var sync=document.createElement("button"); sync.className="rt-tp-sync"; sync.textContent="SYNC";
    sync.setAttribute("aria-label","Sync deck B to deck A");
    sync.addEventListener("click",function(){ var b=document.querySelector(".rt-sync"); if(b) b.click(); });
    var reopen=document.createElement("button"); reopen.id="rt-dock-open";
    reopen.type="button"; reopen.textContent="BOOTH";
    reopen.setAttribute("aria-label","Open the booth");
    t.appendChild(ca); t.appendChild(sync); t.appendChild(cb);
    if(document.querySelector(".rt-wrap.rt-docked, .rt-wrap.rt-rail")) t.appendChild(reopen);
    document.body.appendChild(t);
    var boothVisible=true;
    if("IntersectionObserver" in window){
      new IntersectionObserver(function(en){ boothVisible=en[0].isIntersecting; },{threshold:0}).observe(booth);
    }
    setInterval(function(){
      [ca,cb].forEach(function(c){
        var d=c._els.deck;
        c._els.play.textContent=d.playing?"❚❚":"▶";
        c._els.bpm.textContent=d.rec?d.effBpm().toFixed(1):"—";
        c._els.name.textContent=d.rec?d.rec.title:"no record";
        if(document.activeElement!==c._els.pitch) c._els.pitch.value=d.pitch;
        c.classList.toggle("playing",!!d.playing);
      });
      var collapsed=document.documentElement.classList.contains("rt-booth-collapsed");
      var show=collapsed||(!boothVisible&&(window.RTBooth.decks.a.rec||window.RTBooth.decks.b.rec));
      t.classList.toggle("on",!!show);
      document.documentElement.classList.toggle("rt-tp-on",!!show);
      t.classList.toggle("lift",!!document.querySelector(".mobile-player-container.rt-bar-active"));
      // the video monitor stacks above the transport whatever its row count
      document.documentElement.style.setProperty("--rt-tp-h", show ? (t.offsetHeight||0)+"px" : "0px");
    },300);
  }
  if(document.readyState!=="loading") init(); else document.addEventListener("DOMContentLoaded",init);
})();

/* ---- docked booth: collapse to the mini transport, and back ----
   Collapsing hides the wrap, which stops it intersecting, so the transport's
   own "booth not visible" rule brings it back - no coordination needed beyond
   the class. The choice is remembered because Shopify reloads the whole page
   on every navigation, and re-opening the booth on every click would be
   tiresome. (Playback itself cannot survive a reload - see NEEDLE-DROP.md.) */
(function(){
  "use strict";
  if(!document.querySelector(".rt-wrap.rt-docked, .rt-wrap.rt-rail")) return;
  var KEY="rt-booth-collapsed";
  function apply(collapsed){
    document.documentElement.classList.toggle(KEY, collapsed);
    try{ localStorage.setItem(KEY, collapsed ? "1" : "0"); }catch(e){}
    var b=document.getElementById("rt-dock-toggle");
    if(b) b.setAttribute("aria-expanded", collapsed ? "false" : "true");
  }
  var saved=null;
  try{ saved=localStorage.getItem(KEY); }catch(e){}
  apply(saved === "1");
  document.addEventListener("click", function(e){
    if(!e.target.closest) return;
    var hit=e.target.closest("#rt-dock-toggle, #rt-dock-open");
    if(!hit) return;
    e.preventDefault();
    apply(hit.id === "rt-dock-toggle");
  });
  document.addEventListener("keydown", function(e){
    if(e.key !== "Escape") return;
    if(/^(INPUT|TEXTAREA|SELECT)$/.test((e.target.tagName||""))) return;
    if(!document.documentElement.classList.contains(KEY)) apply(true);
  });
})();
